<template>
    <div id="content-wrap" style="margin-left:90px" :style="{ opacity: !$root.isLoadingData ? 1 : 0 }">
        <MissingDataText v-if="!hasPlayers" :message="'Selected squad is empty'" />
        <div v-else>
            <Header :updateSelection="updatePlayerSelection" />
            <MissingDataText message='No recent rehab session' v-if="!hasData" />
            <div class="row mx-auto justify-content-center" style="width: 90vw;" v-else>
                <div class="d-flex justify-content-end w-100 mt-2">
                    <EditRehabTargetsModal :customerId="customerId" :playerId="selectedPlayer.playerId"
                        :rehabTargets="rehabTargets" :distanceBands="distanceBands" :refreshData="updatePlayerSelection"
                        :targetReferences="targetReferences" />
                </div>
                <div class="d-flex justify-content-end mt-2">
                    <Button id="rehab-sessions-filter-button" type="primary" title="Filter Sessions"
                        v-b-modal.date-picker-edit-modal :onClick="() => { }" />
                </div>
                <div class="w-100 d-flex justify-content-center mt-2">
                    <p>Date Range: {{ formattedISORange().start + ' to ' + formattedISORange().end }}</p>
                </div>
                <div class="col-12 w-100" v-if="maxComparisonKpiData">
                    <LongitudinalKpiComparison :data="maxComparisonKpiData" />
                </div>
                <div class="col-12 col-lg-6 w-100 mt-4" v-if="kpiValuesBySessionId">
                    <LongitudinalKpiAnalysis :sessions="sessions" :data="kpiValuesBySessionId"
                        :comparisonKpiData="comparisonKpiData" />
                </div>
                <div class="col-12 col-lg-6 w-100 mt-4" v-if="sprintCounts">
                    <LongitudinalSprintCount :sessions="sessions" :sprintData="sprintCounts.SPRINT"
                        :hsrData="sprintCounts.HSR" :comparisonCountsData="sprintComparisonData"
                        :speedTypeThresholds="speedTypeThresholds" :distanceBands="distanceBands" />
                </div>
                <div class="col-12 w-100 mt-2">
                    <div class="section-container w-100">
                        <div class="mt-4 mb-4">
                            <Button id="shared-filter-button" type="primary" title="Filter Turns"
                                v-b-modal.scatter-edit-modal :onClick="() => { }" />
                        </div>
                        <SelectedFiltersLabel />
                        <div class="row col-12">
                            <div class="col-lg-6 col-12" v-if="scattersData">
                                <LongitudinalTurnScatters :data="scattersData" :firstRehabDate="firstRehabDate"
                                    :highlightedDates="range" :rangeManuallySelected="rangeManuallySelected" />
                            </div>
                            <div class="col-lg-6 col-12" v-if="turnSummaryData">
                                <LongitudinalSignificantTurnSummary :data="turnSummaryData" />
                            </div>
                        </div>
                        <TurnFilterModal id="scatter-edit-modal" :seasonOptions="seasonOptions"
                            :includeDateFilters="false" />
                    </div>
                </div>
                <div class="col-12 w-100 mt-4" v-if="peakThresholdData">
                    <LongitudinalPeakAnalysis :sessions="sessions" :rainbowData="peakThresholdData"
                        :rainbowComparisonData="peakPhysicalCapacityRainbowData"
                        :peakEpochCountData="peakThresholdCounts"
                        :peakEpochCountComparisonData="peakThresholdCountsComparison" />
                </div>
                <div class="col-12 w-100 mt-2" v-if="accelsDecelsData">
                    <RehabAccelsDecelsCapacity type="accelPercentageLabels" :data="accelsDecelsData"
                        title="Acceleration" mode="multi" />
                    <RehabAccelsDecelsCapacity type="decelPercentageLabels" :data="accelsDecelsData"
                        title="Deceleration" mode="multi" />
                </div>
                <EditModal id="date-picker-edit-modal" title="Select Date Range" :ok="applyFilters"
                    :cancel="updatePlayerSelection" cancel-title="Reset" ok-title="Show Sessions"
                    cancel-variant="danger standard-btn" size="md">
                    <vc-date-picker v-model="tempRange" :first-day-of-week="1" :min-date="new Date(firstRehabDate)"
                        :max-date="new Date(lastRehabDate)" is-range color="teal" is-dark class="mb-3" :attributes="highlightedDates"
                        style="background-color: var(--bg-primary); border: 1px solid var(--sportlight-teal); border-radius: 0;" />
                    <div class="mb-3">
                        <RadioButton :id="'session-type-radio-group'" :modal="sessionTypeSelected"
                            :options="sessionTypeOptions" :name="'epoch-radio-options'"
                            @update-modal="sessionTypeSelected = $event" label="Select Session Type" />
                    </div>
                </EditModal>
            </div>
        </div>
    </div>
</template>

<script>
import Header from "./Header";
import { UserData } from "@/components/UserData";
import UpdateSetting from "@/utils/UpdateSetting";
import LongitudinalSignificantTurnSummary from './LongitudinalSignificantTurnSummary.vue';
import LongitudinalTurnScatters from './LongitudinalTurnScatters.vue';
import LongitudinalKpiComparison from './LongitudinalKpiComparison.vue';
import LongitudinalKpiAnalysis from './LongitudinalKpiAnalysis.vue';
import LongitudinalSprintCount from './LongitudinalSprintCount.vue';
import LongitudinalPeakAnalysis from './LongitudinalPeakAnalysis.vue';
import SelectedFiltersLabel from './SelectedFiltersLabel.vue';
import store from './store';
import { errorHandler } from "@/components/ErrorHandler";
import RehabAccelsDecelsCapacity from "./RehabAccelsDecelsCapacity.vue";
import { getSeasons } from "@/utils/Seasons";
import DateUtils from "@/utils/DateUtils";
import EditRehabTargetsModal from "./EditRehabTargetsModal.vue";

export default {
    components: {
        EditRehabTargetsModal,
        Header,
        LongitudinalSignificantTurnSummary,
        RehabAccelsDecelsCapacity,
        LongitudinalKpiAnalysis,
        LongitudinalTurnScatters,
        LongitudinalKpiComparison,
        LongitudinalSprintCount,
        LongitudinalPeakAnalysis,
        SelectedFiltersLabel
    },
    data() {
        return {
            customerId: UserData.customerId(),
            hasPlayers: false,
            hasData: false,
            turnSummaryData: null,
            accelsDecelsData: null,
            scattersData: null,
            seasonOptions: null,
            maxComparisonKpiData: null,
            sessions: null,
            kpiValuesBySessionId: null,
            comparisonKpiData: null,
            sprintCounts: null,
            sprintComparisonData: null,
            speedTypeThresholds: null,
            distanceBands: null,
            peakThresholdData: null,
            peakPhysicalCapacityRainbowData: null,
            peakThresholdCounts: null,
            peakThresholdCountsComparison: null,
            firstRehabDate: null,
            lastRehabDate: null,
            range: null,
            tempRange: {
                start: null,
                end: null
            },
            recentRehabSessions: null,
            sessionTypeSelected: 'All',
            sessionTypeOptions: [],
            rehabTargets: null,
            targetReferences: null,
            rangeManuallySelected: false,
        };
    },
    methods: {
        async getMostRecentRehabSessions() {
            const response = await this.$root.webApiGet(
                `/recentrehabsessions?customerId=${this.customerId}&playerId=${this.selectedPlayer.playerId}`
            );
            if (response.status === 200) {
                this.hasData = response.data.length > 0;
                if (this.hasData) {
                    this.recentRehabSessions = response.data;
                    this.firstRehabDate = response.data[0].date;
                    this.lastRehabDate = response.data[response.data.length - 1].date;
                }
            }
        },
        async getLongitudinalRehab() {
            if (this.hasData) {
                const startDate = this.formattedISORange().start;
                const endDate = this.formattedISORange().end;
                const sessionTypeIdParam = this.sessionTypeSelected !== 'All' ? `&sessionTypeId=${this.sessionTypeSelected}` : '';
                const response = await this.$root.webApiGet(
                    `/longitudinalrehab?customerId=${this.customerId}&playerId=${this.selectedPlayer.playerId}&startDate=${startDate}&endDate=${endDate}${sessionTypeIdParam}`
                );
                if (response.status === 200) {
                    const { sessions, sessionTurnClassCounts, turnsZScores, accelDecelCapacities, recentRehabTurns, firstTeamMatchTurns, kpiSummary, kpiOverTimeData,
                        sprintThresholdOverTimeData, peakPhysicalCapacityOverTimeData,
                        peakThresholdCountOverTimeData, rehabTargets } = response.data;
                    this.sessions = sessions;
                    this.scattersData = [...recentRehabTurns, ...firstTeamMatchTurns.map(turn => ({ ...turn, sessionIsAMatch: true }))];

                    this.turnSummaryData = { sessionTurnClassCounts, turnsZScores };
                    this.accelsDecelsData = accelDecelCapacities;
                    this.maxComparisonKpiData = kpiSummary;
                    this.comparisonKpiData = kpiOverTimeData.kpiReferenceData;
                    this.kpiValuesBySessionId = kpiOverTimeData.kpiValuesBySessionId;
                    this.distanceBands = sprintThresholdOverTimeData.distanceBands;
                    this.speedTypeThresholds = sprintThresholdOverTimeData.speedTypeThresholds;
                    this.sprintCounts = sprintThresholdOverTimeData.thresholdCountsBySpeedTypeBySessionId;
                    this.sprintComparisonData = sprintThresholdOverTimeData.thresholdReferenceDataBySpeedType;
                    this.peakThresholdData = peakPhysicalCapacityOverTimeData.peakPhysicalCapacityBySessionId;
                    this.peakPhysicalCapacityRainbowData = peakPhysicalCapacityOverTimeData.peakPhysicalCapacityRainbowData;
                    this.peakThresholdCounts = peakThresholdCountOverTimeData.peakThresholdCountBySessionId;
                    this.peakThresholdCountsComparison = peakThresholdCountOverTimeData.peakThresholdCountsReferenceData;
                    this.targetReferences = {
                        sprintCounts: sprintThresholdOverTimeData.thresholdReferenceDataBySpeedType,
                        avgKpis: kpiSummary.avgComparisonValues, maxKpis: kpiSummary.maxComparisonValues
                    };
                    this.rehabTargets = rehabTargets;

                    //Add Rehab targets to the kpis and sprint counts comparison objects by matching the object keys
                    const result = {};
                    Object.keys(rehabTargets.sprintCountTargets).forEach(speedType => {
                        const distanceBandTargets = rehabTargets.sprintCountTargets[speedType];
                        const comparisonDistanceBandData = this.sprintComparisonData?.[speedType];
                        result[speedType] = {};

                        if (comparisonDistanceBandData) {
                            // Populate using existing comparison data structure
                            Object.keys(comparisonDistanceBandData).forEach(band => {
                                result[speedType][band] = {
                                    ...(comparisonDistanceBandData[band] || {}),
                                    target: distanceBandTargets?.[band]
                                };
                            });
                        } else {
                            // Only return target values if player has no metric stats
                            Object.keys(distanceBandTargets || {}).forEach(band => {
                                result[speedType][band] = {
                                    target: distanceBandTargets[band]
                                };
                            });
                        }
                    });
                    this.sprintComparisonData = result;

                    Object.keys(rehabTargets.rehabKpiValues).forEach(kpi => {
                        const targetValue = rehabTargets.rehabKpiValues[kpi];

                        if (this.comparisonKpiData[kpi]) {
                            this.comparisonKpiData[kpi].target = targetValue;
                        } else {
                            this.comparisonKpiData[kpi] = {
                                target: targetValue
                            };
                        }
                    });

                    this.maxComparisonKpiData.targetValues = rehabTargets.rehabKpiValues;
                } else {
                    errorHandler.error(response, this);
                }
            }
        },
        formattedISORange() {
            if (!this.range || !this.range.start || !this.range.end) {
                return this.range = { start: DateUtils.formatDateISO(this.firstRehabDate), end: DateUtils.formatDateISO(this.lastRehabDate) };
            } else {
                const start = DateUtils.formatDateISO(this.range.start);
                const end = DateUtils.formatDateISO(this.range.end);
                return { start, end };
            }
        },
        resetData() {
            this.turnSummaryData = null;
            this.accelsDecelsData = null;
            this.maxComparisonKpiData = null;
            this.comparisonKpiData = null;
            this.sessions = null;
            this.kpiValuesBySessionId = null;
            this.hasData = false;
            this.sprintCounts = null;
            this.sprintComparisonData = null;
            this.speedTypeThresholds = null;
            this.distanceBands = null;
            this.peakThresholdData = null;
            this.peakPhysicalCapacityRainbowData = null;
            this.peakThresholdCounts = null;
            this.peakThresholdCountsComparison = null;
            this.firstRehabDate = null;
            this.lastRehabDate = null;
            this.tempRange = { start: null, end: null };
            this.range = null;
            this.sessionTypeSelected = 'All';
            this.rehabTargets = null;
            this.targetReferences = null;
            this.rangeManuallySelected = false;
        },
        async updatePlayerSelection() {
            await this.$root.executeTaskWithProgressBar(async () => {
                this.resetData();
                await this.getMostRecentRehabSessions();
                this.generateSessionTypeOptions();
                await this.getLongitudinalRehab();
            });
        },
        async applyFilters() {
            await this.$root.executeTaskWithProgressBar(async () => {
                this.range = { ...this.tempRange };
                this.rangeManuallySelected = true;
                await this.getLongitudinalRehab();
            });
        },
        setSeasonOptions() {
            if (this.turnSummaryData) {
                const earliestDate = new Date(Math.min(...this.turnSummaryData.sessionTurnClassCounts.map(session => new Date(session.sessionDate).valueOf())));
                this.seasonOptions = getSeasons(earliestDate, true);
            }
        },
        generateSessionTypeOptions() {
            if (this.recentRehabSessions) {
                const sessionTypes = this.recentRehabSessions.map(t => t.sessionType);
                const uniqueSessionIds = [...new Set(sessionTypes.map(item => item.id))];
                const uniqueSessionTypes = uniqueSessionIds.map(id => sessionTypes.find(item => item.id === id));
                this.sessionTypeOptions = [
                    { text: 'Training & Rehab', value: 'All' },
                    ...uniqueSessionTypes.map(item => ({ text: item.name, value: item.id }))
                ];
            }
        },
    },
    computed: {
        selectedPlayer() {
            return store.state.selectedPlayer;
        },
        highlightedDates() {
            return this.recentRehabSessions.map(session => ({
                key: session.date,
                highlight: true,
                dates: new Date(session.date),
            }));
        }
    },
    async mounted() {
        await this.$root.executeTaskWithProgressBar(async () => {
            console.log('LongitudinalRehab.vue mounted');
            await store.dispatch('getRehabSessions', this);
            await store.dispatch('getPlayers', this);
            this.hasPlayers = store.state.players.length > 0;
            if (this.hasPlayers) {
                await this.getMostRecentRehabSessions();
                this.generateSessionTypeOptions();
                await this.getLongitudinalRehab();
                this.setSeasonOptions();
            }
            UpdateSetting();
        });
    },
};
</script>